import type { FC, ReactNode } from 'react'
import type { ContactStep } from '@/types'

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ContactProgressProps {
    steps: Array<ContactStep>
    current: number
    locked?: boolean
    onSelect: (index: number) => void
}

export const ContactProgress: FC<ContactProgressProps> = ({
    steps,
    current,
    locked = false,
    onSelect
}): ReactNode => {
    return (
        <ol className='grid grid-cols-3 gap-3 sm:gap-4' aria-label='Form progress'>
            {steps.map((step, index) => {
                const complete = index < current
                const active = index === current
                const selectable = complete && !locked

                return (
                    <li key={step.label} className='min-w-0'>
                        <button
                            type='button'
                            onClick={() => onSelect(index)}
                            disabled={!selectable}
                            aria-current={active ? 'step' : undefined}
                            className={cn(
                                'flex w-full min-w-0 items-center gap-2 rounded-t-sm border-b pb-3 text-left text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring sm:text-sm',
                                active || complete ? 'border-primary text-foreground' : 'border-border text-muted-foreground',
                                selectable ? 'cursor-pointer hover:text-primary' : 'cursor-default'
                            )}
                        >
                            <span
                                className={cn(
                                    'hidden size-3.5 shrink-0 items-center justify-center rounded-full border transition-colors min-[440px]:flex',
                                    complete && 'border-primary bg-primary text-primary-foreground',
                                    active && 'border-[1.5px] border-primary',
                                    !active && !complete && 'border-input'
                                )}
                                aria-hidden='true'
                            >
                                {complete && <Check className='size-2.5' strokeWidth={3.5} />}
                            </span>

                            <span className='truncate'>
                                {step.label}
                            </span>

                            <span className='sr-only'>
                                {complete ? '(completed)' : active ? '(current step)' : ''}
                            </span>
                        </button>
                    </li>
                )
            })}
        </ol>
    )
}
